// Uložené AI Recruiter seznamy — snapshoty, ke kterým se manažer může vrátit.
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FloppyDisk, Trash, CaretDown, CaretUp, Plus, Check, ArrowSquareOut } from "@phosphor-icons/react";
import { listRecruiterLists, recruiterListDetail, deleteRecruiterList, addToPool, removePoolByFpl } from "../../lib/lab-api";

export default function SavedRecruiterLists({ onPoolChange }) {
    const [lists, setLists] = useState(null);
    const [open, setOpen] = useState(null);
    const [detail, setDetail] = useState(null);

    const load = useCallback(() => {
        listRecruiterLists().then((d) => setLists(d.items || [])).catch(() => setLists([]));
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggle = async (id) => {
        if (open === id) { setOpen(null); setDetail(null); return; }
        setOpen(id); setDetail(null);
        try { setDetail((await recruiterListDetail(id)).list); }
        catch { toast.error("Načtení seznamu selhalo"); }
    };

    const remove = async (id) => {
        try { await deleteRecruiterList(id); toast.success("Seznam smazán"); if (open === id) { setOpen(null); setDetail(null); } load(); }
        catch { toast.error("Smazání selhalo"); }
    };

    const toPool = async (it) => {
        try {
            await addToPool({ fpl_id: it.fpl_id, status: "scouted" });
            toast.success(`${it.name} přidán do poolu`);
            setDetail((d) => d && { ...d, items: d.items.map((x) => x.fpl_id === it.fpl_id ? { ...x, in_pool: true } : x) });
            onPoolChange?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Přidání selhalo"); }
    };

    const fromPool = async (it) => {
        try {
            await removePoolByFpl(it.fpl_id);
            toast.success(`${it.name} vyřazen z poolu`);
            setDetail((d) => d && { ...d, items: d.items.map((x) => x.fpl_id === it.fpl_id ? { ...x, in_pool: false } : x) });
            onPoolChange?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Vyřazení selhalo"); }
    };

    if (!lists || lists.length === 0) return null;
    return (
        <div className="space-y-2" data-testid="saved-recruiter-lists">
            <h3 className="font-display font-black uppercase text-sm flex items-center gap-1.5">
                <FloppyDisk size={14} className="text-fa-sky" /> Uložené recruiter seznamy
            </h3>
            {lists.map((l) => (
                <div key={l.id} className="border border-white/10 bg-black/40" data-testid={`saved-list-${l.id}`}>
                    <div className="flex items-center justify-between gap-2 p-3">
                        <button type="button" onClick={() => toggle(l.id)} data-testid={`saved-list-toggle-${l.id}`}
                                className="flex-1 text-left flex items-center gap-2">
                            {open === l.id ? <CaretUp size={12} className="text-white/40" /> : <CaretDown size={12} className="text-white/40" />}
                            <div>
                                <span className="font-display font-bold text-sm">{l.label || l.focus_title}</span>
                                <span className="font-mono text-[9px] text-white/45 ml-2">
                                    {l.items_count} kandidátů · {new Date(l.created_at).toLocaleDateString("cs")}
                                </span>
                            </div>
                        </button>
                        <button type="button" onClick={() => remove(l.id)} data-testid={`saved-list-delete-${l.id}`}
                                className="text-white/30 hover:text-red-400"><Trash size={13} /></button>
                    </div>
                    {open === l.id && (
                        <div className="border-t border-white/5 p-3 space-y-2">
                            {!detail && <p className="font-mono text-[10px] text-white/40">Načítám…</p>}
                            {detail?.ai_summary && (
                                <p className="font-mono text-[10px] text-white/60 border-l-2 border-fa-sky pl-2">{detail.ai_summary}</p>
                            )}
                            {(detail?.items || []).map((it) => (
                                <div key={it.fpl_id} className="flex items-center justify-between gap-2 border border-white/5 bg-[#0d0d0d] px-3 py-2"
                                     data-testid={`saved-item-${it.fpl_id}`}>
                                    <div className="flex items-center gap-3">
                                        <span className="font-display font-black text-base text-fa-mint tabular-nums w-8">{Math.round(it.fit_score)}</span>
                                        <div>
                                            <Link to={`/players/${it.fpl_id}`} className="font-display font-bold text-sm hover:text-fa-mint">{it.name}</Link>
                                            <div className="font-mono text-[9px] text-white/45">{it.team} · {it.position} · £{it.price}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {it.in_pool ? (
                                            <button type="button" onClick={() => fromPool(it)} data-testid={`saved-item-remove-${it.fpl_id}`}
                                                    title="Vyřadit z poolu"
                                                    className="font-mono text-[9px] uppercase border border-fa-mint/40 text-fa-mint px-2 py-1 hover:border-red-400/60 hover:text-red-300 transition-colors flex items-center gap-1 group/pool">
                                                <Check size={10} className="group-hover/pool:hidden" />
                                                <span className="group-hover/pool:hidden">v poolu</span>
                                                <span className="hidden group-hover/pool:inline">vyřadit</span>
                                            </button>
                                        ) : (
                                            <button type="button" onClick={() => toPool(it)} data-testid={`saved-item-add-${it.fpl_id}`}
                                                    className="font-mono text-[9px] uppercase border border-fa-mint/40 text-fa-mint px-2 py-1 hover:bg-fa-mint hover:text-black flex items-center gap-1">
                                                <Plus size={10} /> Pool
                                            </button>
                                        )}
                                        <Link to={`/players/${it.fpl_id}`} className="text-white/40 hover:text-white"><ArrowSquareOut size={12} /></Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}
